import React, { useCallback, useEffect, useMemo } from "react";
import { InlineNotification, Link as TextLink, useToast } from "@familyzone/component-library";
import { AttributeMapping, MergeConfig, ProviderData, SISProvider } from "./SisIntegration";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Text,
  Box,
  Button,
  FormControl,
  Select,
  FormErrorMessage,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { MergeOptions, UserMergeKey } from "../SyncConfig/ConfigForm/MergeOptions";
import { extractSyncError } from "../../../utils/api/SyncApi";
import { saveSisSync } from "./mapSyncNodeToSISProvider";
import { Controller, useForm } from "react-hook-form";
import { ConfigFormLabel } from "../SyncConfig/ConfigForm/ConfigFormLabel";
import { colors } from "../../../theme";
import { TextFieldControl } from "../SyncConfig/ConfigForm/TextFieldControl";
import { CheckBoxControl } from "../SyncConfig/ConfigForm/CheckboxControl";
import NuMultiSelect from "../../../utils/NuMultiSelect";
import { get } from "shades";
import {
  AttributeConfig,
  YearAttributes,
  defaultYearAttributes,
  YearAttributesToInput,
  StoredOrDefaultYearAttributes,
} from "./AttributeConfig";

const SisTypes = [
  ["Achieve", "Achieve"],
  ["Aeries", "Aeries"],
  ["Aspen", "Aspen"],
  ["Aspire", "Aspire"],
  ["Blackbaud", "Blackbaud"],
  ["Chalkable_iNOW", "Chalkable iNOW"],
  ["ClassLink", "ClassLink"],
  ["Ellucian", "Ellucian"],
  ["eSchoolPlus", "eSchoolPlus"],
  ["FACTS (RenWEB)", "FACTS (RenWEB)"],
  ["Focus", "Focus"],
  ["Frontline Progressbook", "Frontline Progressbook"],
  ["Genesis", "Genesis"],
  ["Gradelink", "Gradelink"],
  ["Harmony", "Harmony"],
  ["Illuminate_Education", "Illuminate Education"],
  ["Infinite Campus", "Infinite Campus"],
  ["InResonance", "InResonance"],
  ["JCampus", "JCampus"],
  ["JMC", "JMC"],
  ["Lumen_Touch", "Lumen Touch"],
  ["OneRoster", "OneRoster"],
  ["Powerschool", "PowerSchool"],
  ["Rediker", "Rediker"],
  ["Sapphire", "Sapphire"],
  ["GG4L", "SchoolDay"], // GG4L Rebranded to SchoolDay. Formerly "GG4L (Global Grid 4 Learning)"
  ["Schoolrunner", "Schoolrunner"],
  ["Skyward", "Skyward"],
  ["Sycamore", "Sycamore"],
  ["Synergy", "Synergy"],
  ["TxEIS", "TxEIS"],
  ["Tyler", "Tyler"],
  ["Veracross", "Veracross"],
  ["Vision", "Vision SIS"],
  ["unit4", "Unit4"],
  ["Other", "Other"],
];

interface Props {
  rowToEdit: SISProvider | null;
  isClasslink?: boolean;
  show: boolean;
  handleHide: () => void;
  handleSubmit: () => void;
  multipleSyncsConfigured?: boolean;
}

export interface OneRosterInputs {
  sync_classroom_data: boolean;
  sync_parent_data: boolean;
  respect_enrolment_date: boolean;
  respect_sessions: boolean;
  integration_type: string;
  name: string;
  endpoint_url: string;
  token_url: string;
  client_id: string;
  client_secret: string;
  sync_parent_types: string[];
  user_merge_priority: number;
  user_merge_key: UserMergeKey;
  user_merge_expression?: string;
  user_merge_attach_only: boolean;
  sync_year_attributes: boolean;
  year_attribute_mappings?: YearAttributes;
  compatibility_mode: boolean;

  // these values are not editable
  id?: string;
  enabled: boolean;
  excluded_schools_list: string[];
  type: "classlink" | "oneroster";
}

export const SisOneRosterForm: React.FC<Props> = ({
  rowToEdit,
  show,
  handleHide,
  handleSubmit: handleSubmitExternal,
  isClasslink,
  multipleSyncsConfigured = false,
}) => {
  const { successToast, errorToast } = useToast();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    control,
    setError,
    clearErrors,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<OneRosterInputs>({
    mode: "onBlur",
    defaultValues: {
      sync_classroom_data: true,
      sync_parent_data: false,
      respect_enrolment_date: false,
      respect_sessions: false,
      integration_type: isClasslink ? "ClassLink" : "Other",
      name: "",
      endpoint_url: "",
      token_url: "",
      client_id: "",
      client_secret: "",
      sync_parent_types: ["Parent", "Relative", "Guardian"],
      user_merge_priority: 10,
      user_merge_expression: undefined,
      user_merge_key: UserMergeKey.Email,
      user_merge_attach_only: false,
      id: undefined,
      enabled: true,
      excluded_schools_list: [],
      type: isClasslink ? "classlink" : "oneroster",
      sync_year_attributes: true,
      year_attribute_mappings: defaultYearAttributes,
      compatibility_mode: false,
    },
  });

  const syncClassroomDataWatch = watch("sync_classroom_data");
  const syncParentDataWatch = watch("sync_parent_data");
  const syncYearAttributesWatch = watch("sync_year_attributes");
  const mergeKey = watch("user_merge_key");
  const integrationType = watch("integration_type");
  const nameWatch = watch("name");
  const endpointUrlWatch = watch("endpoint_url");
  const clientIdWatch = watch("client_id");
  const clientSecretWatch = watch("client_secret");

  // Submit stays disabled while any required field is blank. The per-field rules
  // still explain which one once it has been touched; this only stops a submit
  // that could never succeed. token_url is recommended, not required.
  // On edit, secrets come back as a `*****hint` mask, so an existing config
  // satisfies client_secret without the user re-entering it.
  const requiredFieldsMissing = useMemo(
    () => [integrationType, nameWatch, endpointUrlWatch, clientIdWatch, clientSecretWatch].some((v) => !v?.trim()),
    [integrationType, nameWatch, endpointUrlWatch, clientIdWatch, clientSecretWatch]
  );

  useEffect(() => {
    if (rowToEdit) {
      reset({
        sync_classroom_data:
          rowToEdit.sync_classrooms !== undefined && rowToEdit.sync_classrooms !== null ? rowToEdit.sync_classrooms : true,
        sync_parent_data: rowToEdit.sync_parents || false,
        respect_enrolment_date: rowToEdit.respect_enrolment_date || false,
        respect_sessions: rowToEdit.respect_sessions || false,
        integration_type: isClasslink ? "ClassLink" : rowToEdit.integration_type || "Other",
        name: rowToEdit.name || "",
        endpoint_url: rowToEdit.api_configuration.endpoint_url || "",
        token_url: rowToEdit.api_configuration.token_url || "",
        client_id: rowToEdit.api_configuration.client_id || "",
        client_secret: rowToEdit.api_configuration.client_secret || "",
        // if 0 set default to all 3, this is so existing users don't need to set these themselves
        // we also make the first char upper to look nicer, we make this lower again on save
        sync_parent_types:
          rowToEdit.sync_parent_types && rowToEdit.sync_parent_types.length > 0
            ? rowToEdit.sync_parent_types.map((value) => value.charAt(0).toUpperCase() + value.slice(1))
            : ["Parent", "Relative", "Guardian"],
        user_merge_priority: rowToEdit.merge_config?.priority || 10,
        user_merge_expression: rowToEdit.merge_config?.computed?.expression || undefined,
        //  if computed exists, set to Computed
        // else set to value of email?.stripped or default of email
        user_merge_key: rowToEdit.merge_config?.computed
          ? UserMergeKey.Computed
          : rowToEdit.merge_config?.email?.stripped
          ? UserMergeKey.EmailStripped
          : UserMergeKey.Email || UserMergeKey.Email,
        user_merge_attach_only: rowToEdit.merge_config?.attachOnly || false, // default is false for this one
        sync_year_attributes: rowToEdit.sync_attributes?.years?.enabled ?? true,
        year_attribute_mappings: StoredOrDefaultYearAttributes(rowToEdit.sync_attributes?.years?.mappings),

        // uneditable values we just pass through to save
        id: rowToEdit.id,
        enabled: rowToEdit ? rowToEdit.enabled : true,
        excluded_schools_list: rowToEdit.excluded_schools_list || [],
        type: isClasslink ? "classlink" : "oneroster",
        compatibility_mode: rowToEdit.compatibility_mode || false,
      });
    } else if (isClasslink) {
      setValue("integration_type", "ClassLink", { shouldTouch: true });
      setValue("type", "classlink", { shouldTouch: true });
    }
  }, [isClasslink, reset, rowToEdit, setValue, show]);

  const onSubmit = useCallback(
    (input: OneRosterInputs) => {
      return new Promise<void>((resolve, reject) => {
        // Cross-field validation: at least one of parents/classrooms must be enabled
        if (!input.sync_parent_data && !input.sync_classroom_data) {
          const msg = "Please select either parent data, classroom data, or both to be synced.";
          setError("sync_parent_data", { type: "validate", message: msg });
          setError("sync_classroom_data", { type: "validate", message: msg });
          resolve();
          return; // abort submit due to validation errors
        }

        // Build mappings and validate duplicates (single pass via YearAttributesToInput)
        const { mappings, duplicateKeys } = YearAttributesToInput(input.year_attribute_mappings);
        if (duplicateKeys.size > 0) {
          setError(`sync_year_attributes`, {
            type: "validate",
            message: "Cannot have duplicate values across inputs.",
          });
          resolve();
          return; // abort submit due to validation errors
        }

        const mergeConfig: MergeConfig = {
          priority: input.user_merge_priority,
          attachOnly: input.user_merge_attach_only,
        };
        if (input.user_merge_key === UserMergeKey.Computed) {
          mergeConfig.computed = {
            expression: input.user_merge_expression || "",
          };
        } else {
          mergeConfig.email = {
            stripped: input.user_merge_key === UserMergeKey.EmailStripped,
          };
        }

        const yearAttributes: AttributeMapping = {
          enabled: input.sync_year_attributes,
          mappings: mappings,
        };

        const apiData: ProviderData = {
          type: input.type,
          enabled: input.enabled,
          sync_parents: input.sync_parent_data,
          sync_parent_types: input.sync_parent_types.map((type) => type.toLowerCase()),
          sync_classrooms: input.sync_classroom_data,
          respect_enrolment_date: input.respect_enrolment_date,
          respect_sessions: input.respect_sessions,
          integration_type: input.integration_type,
          excluded_schools_list: input.excluded_schools_list,
          name: input.name,
          api_configuration: {
            endpoint_url: input.endpoint_url,
            token_url: input.token_url,
            client_id: input.client_id,
            client_secret: input.client_secret,
          },
          merge_config: mergeConfig,
          sync_attributes: {
            years: yearAttributes,
          },
          compatibility_mode: input.compatibility_mode,
        };

        const defaultMsg = "An unknown error occurred while trying to save configuration.";
        const onSuccess = () => {
          successToast({ title: `Saved ${integrationType} configuration` });
          handleSubmitExternal();
          resolve();
        };
        const onFailure = (msg: string) => {
          errorToast({ title: `Failed to save ${integrationType} configuration`, description: msg });
          reject(msg);
        };

        // SM-API: POST /api/syncs (create) or PATCH /api/syncs/{id} (update).
        saveSisSync(apiData, input.id)
          .then(onSuccess)
          .catch((e) => onFailure(extractSyncError(e, defaultMsg)));
      });
    },
    [setError, successToast, integrationType, handleSubmitExternal, errorToast]
  );

  const handleHideClick = () => {
    handleHide();
  };

  return (
    <Modal isOpen={show} onClose={handleHideClick} isCentered scrollBehavior={"inside"}>
      <ModalOverlay />
      {/*https://github.com/orgs/react-hook-form/discussions/8622*/}
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalContent maxWidth={"800px"} maxHeight={"80%"}>
          <ModalHeader
            sx={{
              display: "flex",
              flexDir: "row",
              justifyContent: "space-between",
            }}
          >
            {!rowToEdit
              ? "Configure OneRoster or ClassLink Sync"
              : rowToEdit.type === "oneroster"
              ? "Edit OneRoster Sync"
              : "Edit ClassLink Sync"}
            <img
              alt="Close modal button"
              className="modal-close-icon"
              width="13"
              height="13"
              src="/static/images/icon_modal_close.svg"
              onClick={handleHideClick}
            />
          </ModalHeader>
          <ModalBody
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              overflow: "auto",
              flex: 1,
            }}
          >
            {rowToEdit?.read_error && (
              <InlineNotification
                status="error"
                notificationTitle={"This configuration could not be read"}
                notificationDescription={`${rowToEdit.read_error}. The fields below show defaults, not the stored values — saving will replace the entire configuration.`}
              />
            )}
            {rowToEdit && rowToEdit.type === "oneroster" && (
              <TextLink isExternal textAlign={"center"} href={"https://help.linewize.com/hc/en-gb/articles/5733217613596"}>
                Configuring OneRoster integration Guide
              </TextLink>
            )}
            {rowToEdit && rowToEdit.type === "classlink" && (
              <TextLink isExternal textAlign={"center"} href={"https://help.linewize.com/hc/en-gb/articles/5733219087132"}>
                Configuring ClassLink integration Guide
              </TextLink>
            )}
            {!rowToEdit && !isClasslink && (
              <TextLink isExternal textAlign={"center"} href={"https://help.linewize.com/hc/en-gb/articles/5733217613596"}>
                Configuring OneRoster integration Guide
              </TextLink>
            )}
            {!rowToEdit && isClasslink && (
              <TextLink isExternal textAlign={"center"} href={"https://help.linewize.com/hc/en-gb/articles/5733219087132"}>
                Configuring ClassLink integration Guide
              </TextLink>
            )}
            <Text textAlign={"center"}>
              {"Provide your SIS Provider account details below to integrate your school system with Linewize Filter"}
            </Text>

            <Box sx={{ display: "flex", flexDirection: "column", padding: "24px", gap: "16px", fontSize: "14px" }}>
              {!isClasslink && (
                <Controller
                  control={control}
                  name={"integration_type"}
                  rules={{ required: "Integration type is required." }}
                  render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => (
                    <FormControl
                      isInvalid={!!errors.integration_type}
                      sx={{
                        display: "flex",
                        flexDir: "column",
                        minHeight: "40px",
                        gap: "4px",
                      }}
                    >
                      <ConfigFormLabel label={"Which SIS would you like to integrate?"} />
                      <Select
                        data-testid={"integrationType-select"}
                        name={name}
                        ref={ref}
                        iconSize={"0"}
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        size="lg"
                        variant={"outline"}
                        sx={{
                          width: "100%",
                          height: "40px",
                          backgroundColor: "white",
                          borderRadius: "6px",
                          border: `1px solid ${colors.neutrals["30"]}`,
                          paddingLeft: "16px",
                        }}
                      >
                        {SisTypes.map((st) => (
                          <option key={st[0]} value={st[0]}>
                            {st[1]}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage>{error && error.message}</FormErrorMessage>
                    </FormControl>
                  )}
                />
              )}

              <TextFieldControl
                styleType={"column"}
                fieldProps={{
                  name: "name",
                  label: "Name",
                  tooltip: "The name of this SIS Sync Configuration.",
                  control: control,
                  errors: errors,
                  rules: {
                    required: "A name is required.",
                  },
                }}
              />

              <TextFieldControl
                styleType={"column"}
                fieldProps={{
                  name: "endpoint_url",
                  label: "Endpoint URL",
                  tooltip: "The endpoint URL for your SIS provider without /ims/oneroster/v1p1 suffix",
                  control: control,
                  errors: errors,
                  rules: {
                    required: "A endpoint URL is required.",
                  },
                }}
              />

              <TextFieldControl
                styleType={"column"}
                fieldProps={{
                  name: "token_url",
                  label: "OAuth2 Token URL (Recommended)",
                  tooltip: "The endpoint URL for fetching the OAuth2 token. Leave blank if using Basic Auth",
                  control: control,
                  errors: errors,
                }}
              />

              <TextFieldControl
                styleType={"column"}
                fieldProps={{
                  name: "client_id",
                  label: "Client ID",
                  tooltip: "OAuth2 Client ID for your SIS provider",
                  control: control,
                  errors: errors,
                  rules: {
                    required: "A client ID is required.",
                  },
                }}
              />

              <TextFieldControl
                styleType={"column"}
                fieldProps={{
                  name: "client_secret",
                  label: "Client Secret",
                  tooltip: "OAuth2 Client Secret for your SIS provider",
                  control: control,
                  errors: errors,
                  rules: {
                    required: "A client secret is required.",
                  },
                }}
              />

              <CheckBoxControl
                fieldProps={{
                  name: "sync_classroom_data",
                  label: "Sync Classroom Data",
                  tooltip: "When this is ticked, syncs include classrooms and classroom relationships",
                  control: control,
                  errors: errors,
                  rules: {
                    validate: (v: boolean) =>
                      v || syncParentDataWatch || "Please select either parent data, classroom data, or both to be synced.",
                  },
                }}
              />

              {syncClassroomDataWatch && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                    paddingLeft: "20px",
                  }}
                >
                  <CheckBoxControl
                    fieldProps={{
                      name: "respect_enrolment_date",
                      label: "Respect Enrolment Date",
                      tooltip:
                        "When ticked, student enrolment start dates must be before today and end dates must be after today. All other enrolments are not synced for the class.",
                      control: control,
                      errors: errors,
                    }}
                  />

                  <CheckBoxControl
                    fieldProps={{
                      name: "respect_sessions",
                      label: "Respect Academic Sessions",
                      tooltip: "When this is ticked, syncs exclude classes lacking an active academic session.",
                      control: control,
                      errors: errors,
                    }}
                  />
                </Box>
              )}
              <CheckBoxControl
                fieldProps={{
                  name: "sync_parent_data",
                  label: "Sync Parent Data",
                  tooltip: "When this is ticked, syncs include parent/guardian data.",
                  control: control,
                  errors: errors,
                  rules: {
                    validate: (v: boolean) =>
                      v || syncClassroomDataWatch || "Please select either parent data, classroom data, or both to be synced.",
                  },
                }}
              />

              {syncParentDataWatch && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                    paddingLeft: "20px",
                  }}
                >
                  <Controller
                    control={control}
                    name="sync_parent_types"
                    rules={{
                      validate: (vals) =>
                        !syncParentDataWatch || (Array.isArray(vals) && vals.length > 0) || "Required at least one guardian type",
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <FormControl
                        isInvalid={!!error}
                        sx={{
                          display: "flex",
                          flexDir: "column",
                          minHeight: "40px",
                          gap: "4px",
                        }}
                      >
                        <ConfigFormLabel
                          label={"Select relationship type"}
                          tooltip={"Please select at least one relationship type to sync parent data"}
                        />
                        <NuMultiSelect
                          allItems={[{ value: "Parent" }, { value: "Relative" }, { value: "Guardian" }]}
                          selectedValues={value || []}
                          displayTextLens={get("value")}
                          valueLens={get("value")}
                          onAdd={(item: { value: string }) => {
                            const v = get("value")(item);
                            const current = Array.isArray(value) ? value : [];
                            if (!current.includes(v)) {
                              onChange([...current, v]);
                            }
                          }}
                          onRemove={(val: string) => {
                            const current = Array.isArray(value) ? value : [];
                            onChange(current.filter((x) => x !== val));
                          }}
                          searchPlaceholder={"Search relationship types"}
                          data-testid={"syncParentTypes-multiselect"}
                        />
                        <FormErrorMessage>{error && error.message}</FormErrorMessage>
                      </FormControl>
                    )}
                  />
                </Box>
              )}

              <AttributeConfig clearErrors={clearErrors} syncYearAttributes={syncYearAttributesWatch} errors={errors} control={control} />

              <Accordion
                allowToggle
                sx={{
                  border: `1px solid ${colors.neutrals["20"]}`,
                  borderRadius: "6px",
                }}
              >
                <AccordionItem>
                  <AccordionButton type="button" data-testid={"accordionBtn"}>
                    <Box
                      sx={{
                        display: "flex",
                        width: "100%",
                        minHeight: "40px",
                        backgroundColor: colors.neutrals["20"],
                        alignContent: "center",
                        alignItems: "center",
                        padding: "12px",
                        gap: "8px",
                      }}
                    >
                      <AccordionIcon />

                      <Text fontWeight={500}>Advanced Configuration</Text>
                    </Box>
                  </AccordionButton>
                  <AccordionPanel
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      padding: "16px",
                      fontSize: "14px",
                      gap: "16px",
                    }}
                  >
                    <Text>
                      Advanced sync settings are for complex deployments only. Consult with the Linewize support team before making any
                      changes.
                    </Text>
                    <Divider borderColor="neutrals.40" />
                    <MergeOptions errors={errors} register={register} control={control} mergeKey={mergeKey} />
                    <Divider borderColor="neutrals.40" />
                    <InlineNotification
                      status="warning"
                      notificationTitle={"For existing deployments, compatibility mode may cause unexpected changes in sync behaviour."}
                      notificationDescription={
                        <>
                          Compatibility Mode only consumes OneRoster v1.1 endpoints that IMS Global states <strong>must</strong> be
                          supported for rostering conformance.
                        </>
                      }
                    />
                    <CheckBoxControl
                      fieldProps={{
                        name: "compatibility_mode",
                        label: "Compatibility mode",
                        control: control,
                        errors: errors,
                      }}
                    />
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </Box>
          </ModalBody>

          <ModalFooter
            sx={{
              display: "flex",
              flexDirection: "column-reverse",
              justifyContent: "space-between",
              gap: "0.5rem",
            }}
          >
            <Button
              data-testid={"submitBtn"}
              mt={4}
              disabled={!isDirty || isSubmitting || requiredFieldsMissing}
              colorScheme="teal"
              type="submit"
              variant={"primary"}
            >
              Submit
            </Button>
            {multipleSyncsConfigured && (
              <InlineNotification
                status="warning"
                notificationTitle={"Important"}
                notificationDescription={
                  "Multiple SIS Integrations are configured. If multiple syncs are required, ensure they do not duplicate data such as Students, Guardians, Schools or Classrooms."
                }
              />
            )}
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
};
